import React,{useState,createRef,useEffect} from "react";
import { useHistory } from "../utils/router";
import FlagIcon from "../utils/FlagIcon.js";
import { connect } from "react-redux";
import {
  setLoading,
  createSession,
  updateLocalSession
} from "../store/features/session/sessionSlice";
import { setError } from "../store/features/error/errorSlice";
import {
  setUser,
  setLandingPage,
  setVoipDeactive,
} from "../store/features/user/userSlice";
import CssBaseline from "@mui/material/CssBaseline";
import { Link } from "react-router-dom";
import { createTheme, lighten, darken } from "@mui/material/styles";
import { makeStyles } from 'tss-react/mui';
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { useDarkMode } from "../utils/themeMode";
import { themeObjectDark, themeObjectLight } from "../theme";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Paper from "@mui/material/Paper";
import { Formik, Form, Field } from "formik";
import {
  Button,
  InputLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  FormHelperText,
  CircularProgress
} from "@mui/material";
import { TextField } from "formik-mui";
import { attemptLogin, sha256Hex, getLandingPage } from '../utils/utils';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from "@mui/icons-material/Storage";
import alkemyLogo from "../assets/images/alkemy_dev_icon.png"
import Logo from "../assets/images/New-FoneLogix-Logo.png";

const mapDispatchToProps = {
  setLoading,
  createSession,
  updateLocalSession,
  setUser,
  setLandingPage,
  setVoipDeactive,
  setError,
};

const useStyles = makeStyles()((theme, { prefersDarkMode }) => ({
  root: {
    minHeight: "var(--app-vh)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3, 2),
  },
  paper: {
    width: "100%",
    maxWidth: 420,
    padding: theme.spacing(4, 3, 3),
    borderRadius: 14,
    boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.22)",
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(5, 5, 4),
    },
  },
  logo: {
    display: "block",
    maxWidth: "100%",
    maxHeight: 96,
    margin: "0 auto",
    objectFit: "contain",
  },
  // The built-in logo has dark lettering, so on a dark card it sits on a
  // light plate. A tenant's own dark-mode logo does not need one.
  logoPlate: {
    padding: theme.spacing(1, 2),
    borderRadius: 10,
    backgroundColor: "#fff",
  },
  heading: {
    marginTop: theme.spacing(3),
    fontSize: 22,
    fontWeight: 500,
    lineHeight: 1.3,
    textAlign: "center",
    color: theme.palette.text.primary,
  },
  subheading: {
    marginTop: 4,
    marginBottom: theme.spacing(3),
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  // The chosen server: its flag or icon, then its name.
  serverValue: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    "& .flag-icon": {
      width: "1.4em",
      height: "1.05em",
      borderRadius: 2,
      backgroundSize: "cover",
    },
  },
  icon: {
    color: theme.palette.primary.light,
    fontSize: 20,
  },
  visibility: {
    color: theme.palette.text.secondary,
  },
  loginButton: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1.25),
    fontSize: 15,
    fontWeight: 500,
    textTransform: "none",
    boxShadow: "none",
    background: prefersDarkMode
      ? theme.palette.primary.light
      : theme.palette.primary.main,
    color: "white",
    "&:hover": {
      boxShadow: "none",
      background: prefersDarkMode
        ? darken(theme.palette.primary.light, 0.25)
        : darken(theme.palette.primary.main, 0.25),
    },
  },
  spinner: {
    color: "inherit",
    marginRight: theme.spacing(1),
  },
  loginHelp: {
    marginTop: theme.spacing(3),
    fontSize: 13.5,
    lineHeight: 1.9,
    textAlign: "center",
    color: theme.palette.text.secondary,
    "& a": {
      fontWeight: 500,
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  devBy: {
    position: "fixed",
    width: "35px",
    height: "auto",
    background: "white",
    border: "1px solid #dedede",
    padding: "3px",
    borderRadius: "50%",
    bottom: theme.spacing(1.5),
    right: theme.spacing(3),
  },
}));

const Login = (props)=> {
  let history = useHistory();
  const passRef = createRef();
  const {
    setLoading,
    createSession,
    updateLocalSession,
    setUser,
    setLandingPage,
    // setVoipDeactive,
    lastServer,
    session,
    setError,
    branding,
  } = props;
  const prefersDarkMode = useDarkMode();
  let theme = prefersDarkMode ? themeObjectDark : themeObjectLight;

  const typography = {
    fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
    htmlFontSize: 16,
    fontSize: 16,
    body1: {
      fontFamily: "Roboto",
      color: theme.palette.text.softer,
      fontSize: "16px",
      lineHeight: "1.8",
      "& a": {
        color: prefersDarkMode
          ? branding.colors.darkMode.light
          : branding.colors.lightMode.light,
        textDecoration: "none",
      },
    },
  };

  const themeConfig = React.useMemo(
    () =>
      createTheme({
        typography,
        components: {
          // The v4 Grid forced width: 100% on containers; the current Grid does not,
          // which lets table views shrink to their content inside flex rows.
          MuiGrid: {
            styleOverrides: {
              container: {
                width: "100%",
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: lighten(theme.palette.primary.light, 0.9),
                },
                "&.MuiTableRow-hover:hover": {
                  backgroundColor: lighten(theme.palette.primary.light, 0.9),
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                "&:hover": {
                  backgroundColor: lighten(theme.palette.primary.light, 0.75),
                },
                "&.Mui-checked, &.Mui-checked:hover": {
                  "& svg": {
                    color: theme.palette.primary.light,
                  },
                },
              },
              colorSecondary: {
                "&.Mui-checked": {
                  "&:hover": {
                    backgroundColor: lighten(theme.palette.primary.light, 0.75),
                  },
                },
              },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                padding: ".2em .8em",
                border: "2px solid white",
                color: "white",
                backgroundColor: `rgba(theme.palette.primary.main,.5)`,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.light,
                  color: "white",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light,
                    color: "white",
                  },
                },
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                  color: "white",
                },
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: {
                color: theme.palette.text.secondary,
              },
              indicator: {
                backgroundColor: theme.palette.primary.light,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                "&.Mui-selected": {
                  color: theme.palette.primary.light,
                },
                "&.Mui-disabled": {
                  color: "rgba(0,0,0,.35)",
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                "&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.light,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.light,
                },
              },
            },
          },
        },
        palette: {
          ...theme.palette,
          mode: prefersDarkMode ? "dark" : "light",
          primary: {
            ...theme.palette.primary,
            light: prefersDarkMode
              ? branding.colors.darkMode.light
              : branding.colors.lightMode.light,
            main: prefersDarkMode
              ? branding.colors.darkMode.main
              : branding.colors.lightMode.main,
            dark: prefersDarkMode
              ? branding.colors.darkMode.dark
              : branding.colors.lightMode.dark,
          },
          background: {
            ...theme.palette.background,
            // paper: prefersDarkMode
            //   ? branding.colors.darkMode.light
            //   : branding.colors.lightMode.light,
            // Dark mode keeps the app's own dark page behind the card.
            default: prefersDarkMode
              ? theme.palette.background.default
              : branding.colors.lightMode.dark,
          },
        },
      }),
    // eslint-disable-next-line
    [prefersDarkMode]
  );

  const { classes, cx } = useStyles({ prefersDarkMode });
  const [showPassword,setShowPassword] = useState(false);
  const [server, setServer] = useState("");
  const [hasServerSelectionError,setServerSelectionError] = useState(false);
  const [photo, setPhoto] = useState("");

  useEffect(() => {
    
    if (prefersDarkMode && branding.logoDark!=="") {
      setPhoto(
        `https://${session.lastServer}4.admin.my-cloudline.net:9003/${branding.logoDark}`
      );
    }else if (!prefersDarkMode && branding.logoLight !== "") {
      setPhoto(
        `https://${session.lastServer}4.admin.my-cloudline.net:9003/${branding.logoLight}`
      );
    }else{
      setPhoto(Logo);
    }
    // eslint-disable-next-line
  }, [branding, prefersDarkMode]);

  useEffect(()=>{
    if(session && server==="" && lastServer!=="") setServer(lastServer);
    // eslint-disable-next-line
  },[server,session]);

  const handleValidation = (values) => {
    const errors = {};
    
    if (!values.username) {
      errors.username = "User ID Required";
    } 

    if (!values.password) {
      errors.password = "Password Required";
    }

    if(server.length===0){
      setServerSelectionError(true);
    }

    return errors;
  }

  const handleServerSelect = (e)=>{
    setServer(e.target.value);
    setServerSelectionError(false);
  }

  const handleSubmitLogin = (values, {setSubmitting}) => {
    setLoading(true);
    if(server.length>0){
      setServerSelectionError(false);
      values.server=server;

      let response = attemptLogin(values);
  
      response
        .then(async (data) => {
          data.server = server;
          if (data.hasOwnProperty("userProfile")){
            data.voipX = await sha256Hex(values.password);
            createSession(data);
            updateLocalSession(data);
            setUser(data);
            setSubmitting(false);
            setLoading(false);
            // setVoipDeactive();
            return data;
          } else{
            throw data;
          }
        })
        .then((data) => {
          const landingPage = getLandingPage(data.userProfile)
          setLandingPage(landingPage)
          history.push(`/dashboard/${landingPage}`)
        })
        .catch((e) =>{
          setError({
            visible: true,
            type: "error",
            message: `${e}`,
          })
          setSubmitting(false);
          setLoading(false);
        });
    }else{
      setServerSelectionError(true);
      setSubmitting(false);
      setLoading(false);
    }
  };

  const getInputAdornment = () => (
    <InputAdornment position="end">
      <Tooltip title={showPassword ? "Hide password" : "Show password"}>
        <IconButton
          aria-label={showPassword ? "hide password" : "show password"}
          className={classes.visibility}
          edge="end"
          size="small"
          onClick={() => togglePasswordVisibility()}
          onMouseDown={(e) => e.preventDefault()}
        >
          {showPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const togglePasswordVisibility = () => {
    let field = passRef.current
    if (field.type === "password") {
      setShowPassword(true);
      field.type = "text";
    } else {
      setShowPassword(false);
      field.type = "password";
    }
  }

  const serverOptionsList = [
    { title: "United States", icon: "us" },
    { title: "Germany", icon: "de" },
    { title: "Development", icon: "dev" },
  ];

  const getFlagIcon = (code)=>{
    switch(code){
      case "de":
      case "us":
        return <FlagIcon code={code} />;
      case "dev":
        return <CodeIcon className={classes.icon}/>;
      default:
        return <StorageIcon className={classes.icon} />;
    }
  }

  const renderServer = (code) => {
    const option = serverOptionsList.find((item) => item.icon === code);
    return (
      <span className={classes.serverValue}>
        {getFlagIcon(code)}
        {option ? option.title : ""}
      </span>
    );
  }

  return (
    <>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={themeConfig}>
          <CssBaseline />
          <div className={classes.root}>
            {!session.isLoading ? (
              <Paper className={classes.paper}>
                <img
                  src={photo || Logo}
                  alt={`${branding.companyName} logo`}
                  className={cx(classes.logo, prefersDarkMode && (photo || Logo) === Logo && classes.logoPlate)}
                  onError={() => setPhoto(Logo)}
                />
                <div className={classes.heading}>Sign in</div>
                <div className={classes.subheading}>
                  Enter your user ID and password to continue.
                </div>
                <Formik
                  initialValues={{
                    username: "",
                    password: "",
                    server: lastServer,
                  }}
                  validate={handleValidation}
                  onSubmit={(values, setSubmitting) =>
                    handleSubmitLogin(values, setSubmitting)
                  }
                >
                  {({ isSubmitting, isValid, dirty }) => (
                    <Form className={classes.form} noValidate>
                      <Field
                        variant="outlined"
                        component={TextField}
                        fullWidth
                        autoFocus
                        autoComplete="username"
                        name="username"
                        type="text"
                        label="User ID"
                      />
                      <Field
                        variant="outlined"
                        component={TextField}
                        fullWidth
                        type="password"
                        label="Password"
                        name="password"
                        autoComplete="current-password"
                        slotProps={{ input: {
                          endAdornment: getInputAdornment(),
                          inputRef: passRef,
                        } }}
                      />
                      <FormControl fullWidth error={hasServerSelectionError}>
                        <InputLabel id="server-select-label">Server</InputLabel>
                        <Select
                          labelId="server-select-label"
                          id="server-select"
                          label="Server"
                          value={server}
                          onChange={(e) => handleServerSelect(e)}
                          renderValue={renderServer}
                        >
                          {serverOptionsList.map((item) => (
                            <MenuItem key={item.title} value={item.icon}>
                              {renderServer(item.icon)}
                            </MenuItem>
                          ))}
                        </Select>
                        {hasServerSelectionError && (
                          <FormHelperText>
                            Server selection is required
                          </FormHelperText>
                        )}
                      </FormControl>
                      <Button
                        type="submit"
                        variant="contained"
                        className={classes.loginButton}
                        fullWidth
                        disabled={isSubmitting || !(isValid && dirty) || server.length===0 }
                      >
                        {isSubmitting && <CircularProgress size={18} thickness={5} className={classes.spinner} />}
                        {isSubmitting ? "Signing in…" : "Sign in"}
                      </Button>
                    </Form>
                  )}
                </Formik>
                <div className={classes.loginHelp}>
                  Don&apos;t remember your credentials?{" "}
                  <Link
                    color="primary"
                    to="/login"
                    onClick={() => {
                      window.open(`mailto:support@my-cloudline.com?subject=Forgot Password`)
                    }}>
                    Forgot password
                  </Link>
                  <br></br>
                  Not a current user?{" "}
                  <Link
                    color="primary"
                    to="/login"
                    onClick={() => {
                      window.open(`mailto:support@my-cloudline.com?subject=Request Access`)
                    }}>
                    Request access
                  </Link>
                </div>
              </Paper>
            ) : <div></div>}
          </div>

          <a
            href="https://www.alkemydev.com"
            rel="noreferrer noopener"
            target="_blank"
          >
            <Tooltip title="Developed by Alkemy">
              <img
                src={alkemyLogo}
                alt="developed by alkemy"
                className={classes.devBy}
              />
            </Tooltip>
          </a>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
}

const mapStateToProps = (state) => {
  return { 
    session: state.session, 
    lastServer: state.session.lastServer,
    branding: state.branding,
  };
};

export default connect(mapStateToProps,mapDispatchToProps)(Login);
